import React, { useState, useEffect, useCallback } from "react";
import { Container } from "reactstrap";
import { DataTable, IconAvatar, StatusBadge } from "../../components/Common/PageWidgets";
import ActivityDetailModal from "../../components/Common/ActivityDetailModal";
import EmptyState from "../../components/Common/EmptyState";

//Import API
import endpoints from "../../api/endpoints";
import { get } from "../../api/axios";

//Utils
import { extractErrorMessage } from "../../utils/errorHandler";

//Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";

import coinIcon from "../../assets/images/user-dashboard/voucher.svg";
import ticketIcon from "../../assets/images/user-dashboard/redeem.svg";
import starIcon from "../../assets/images/user-dashboard/star.svg";
import eyeIcon from "../../assets/images/support/eye.svg";

/* -------------------------------------------------------------------------
 * Points Activity Table Columns
 * ---------------------------------------------------------------------- */

const ACTIVITY_ICON = {
  referral_referrer: { icon: starIcon, tone: "teal" },
  referral_referee: { icon: starIcon, tone: "teal" },
  voucher_redemption: { icon: ticketIcon, tone: "orange" },
  manual_adjustment: { icon: coinIcon, tone: "blue" },
  passport: { icon: starIcon, tone: "purple" },
  streak: { icon: starIcon, tone: "teal" },
  game: { icon: starIcon, tone: "orange" },
};

const LEDGER_TYPE_LABELS = {
  transaction: "Transaction",
  referral_referrer: "Referral Bonus",
  referral_referee: "Referral Bonus",
  voucher_redemption: "Voucher Redemption",
  manual_adjustment: "Manual Adjustment",
  passport: "Passport Reward",
  streak: "Streak Reward",
  game: "Game Reward",
};

const SOURCE_TYPE_LABELS = {
  "App\\Models\\FinancialTransaction": "Financial Transaction",
  financial_transaction: "Financial Transaction",
  transaction: "Financial Transaction",
  voucher: "Voucher",
  "App\\Models\\Voucher": "Voucher",
  referral: "Referral",
  admin_manual_adjustment: "Admin Manual Adjustment",
  "App\\Models\\UserGameProgress": "Game Progress",
  game: "Game Progress",
  "App\\Models\\UserStreak": "Streak",
  streak: "Streak",
  "App\\Models\\UserPassportReward": "Passport",
  passport: "Passport",
};

const toTitleCase = (value) => (
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const formatSourceType = (value) => {
  if (!value) return "-";
  if (SOURCE_TYPE_LABELS[value]) return SOURCE_TYPE_LABELS[value];

  const className = String(value).split("\\").pop();
  return SOURCE_TYPE_LABELS[className] || toTitleCase(className);
};

const formatDateTime = (value) => {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const datePart = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

  return `${datePart} ${timePart}`;
};

const activityColumns = (onView) => [
  {
    key: "type",
    label: "Name",
    width: "2fr",
    render: (row) => {
      const { icon, tone } = ACTIVITY_ICON[row.type] || ACTIVITY_ICON.manual_adjustment;
      return (
        <span className="data-table__row-label">
          <IconAvatar icon={icon} tone={tone} size={32} />
          {LEDGER_TYPE_LABELS[row.type] || toTitleCase(row.type)}
        </span>
      );
    },
  },
  {
    key: "source_type",
    label: "Type",
    width: "1fr",
    render: (row) => row.sourceTypeLabel,
  },
  {
    key: "status",
    label: "Status",
    width: "1fr",
    render: (row) => <StatusBadge status={row.statusLabel} />,
  },
  {
    key: "points",
    label: "Points",
    width: "1fr",
    render: (row) => (
      <span style={{ color: row.status === 'credit' ? '#1D9C87' : '#d11f1f', fontWeight: 600 }}>
        {row.pointsDisplay}
      </span>
    ),
  },
  {
    key: "dateLabel",
    label: "Date",
    width: "1fr",
  },
  {
    key: "view",
    label: "",
    width: "64px",
    align: "center",
    render: (row) => (
      <button
        type="button"
        onClick={() => onView && onView(row.id)}
        style={{ border: "none", background: "none", cursor: "pointer", padding: 4 }}
        aria-label={`View ${row.typeLabel || "activity"}`}
      >
        <img src={eyeIcon} alt="" style={{ width: 18, height: 18 }} />
      </button>
    ),
  },
];

/* -------------------------------------------------------------------------
 * PointsActivityPage
 * ---------------------------------------------------------------------- */

const PointsActivityPage = () => {
  document.title = "Points Activity | Omaha Food Lovers";

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);

  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreTransactions, setHasMoreTransactions] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [loadingMoreTransactions, setLoadingMoreTransactions] = useState(false);

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Fetch transactions from API
  const fetchTransactions = useCallback(async (page = 1, isLoadMore = false) => {
    const setLoading = isLoadMore ? setLoadingMoreTransactions : setLoadingTransactions;

    setLoading(true);
    try {
      const response = await get(`${endpoints.pointsLedger}?page=${page}`);

      if (response.data.success) {
        const transactionsData = response.data.data;
        const newTransactions = transactionsData.data || [];

        // Transform API response to match component's expected format
        const transformedTransactions = newTransactions.map((tx) => {
          const status = tx.status === "debit" ? "debit" : "credit";
          const points = Math.abs(Number(tx.points || 0));
          const sourceTypeLabel = formatSourceType(tx.source_type);

          return {
            id: tx.id,
            type: tx.type,
            typeLabel: LEDGER_TYPE_LABELS[tx.type] || toTitleCase(tx.type),
            status,
            statusLabel: status === "credit" ? "Credit" : "Debit",
            source_type: tx.source_type,
            sourceTypeLabel,
            points,
            pointsDisplay: `${status === "credit" ? "+" : "-"}${points.toLocaleString()}`,
            dateLabel: formatDateTime(tx.created_at),
            created_at: tx.created_at,
            meta: tx.meta,
          };
        });

        // Append data for infinite scroll, replace for initial load
        if (isLoadMore) {
          setTransactions((prev) => [...prev, ...transformedTransactions]);
        } else {
          setTransactions(transformedTransactions);
        }

        setCurrentPage(transactionsData.current_page || 1);
        setHasMoreTransactions(
          transactionsData.current_page < transactionsData.last_page
        );
      }
    } catch (err) {
      console.error("Failed to fetch transactions:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
      // Use empty array as fallback
      if (!isLoadMore) {
        setTransactions([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch transactions on mount
  useEffect(() => {
    fetchTransactions(1, false);
  }, [fetchTransactions]);

  // Infinite scroll handler
  const handleLoadMoreTransactions = useCallback(() => {
    if (hasMoreTransactions && !loadingMoreTransactions) {
      fetchTransactions(currentPage + 1, true);
    }
  }, [hasMoreTransactions, loadingMoreTransactions, currentPage, fetchTransactions]);

  const handleViewActivity = (activityId) => {
    const act = transactions.find(a => a.id === activityId);
    if (act) {
      const { icon } = ACTIVITY_ICON[act.type] || ACTIVITY_ICON.manual_adjustment;
      const modalActivityData = {
        icon,
        statusLabel: act.statusLabel,
        title: act.typeLabel,
        description: act.sourceTypeLabel,
        type: act.sourceTypeLabel,
        event_label: act.statusLabel,
        date: act.dateLabel,
        points: act.status === 'debit' ? -act.points : act.points,
        pointsTone: act.status === 'credit' ? 'green' : 'red',
      };
      setSelectedActivity(modalActivityData);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedActivity(null);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <div className="wallet-section-header">
          <h2 className="wallet-section-header__title">Points Activity</h2>
        </div>

        <div className="activity-list">
          {loadingTransactions ? (
            <DataTable 
              columns={activityColumns(handleViewActivity)} 
              rows={[]} 
              getRowKey={(row) => row.id}
              loading={true}
            />
          ) : transactions && transactions.length > 0 ? (
            <DataTable
              columns={activityColumns(handleViewActivity)}
              rows={transactions}
              getRowKey={(row) => row.id}
              loading={loadingTransactions}
              loadingMore={loadingMoreTransactions}
              onLoadMore={handleLoadMoreTransactions}
              hasMore={hasMoreTransactions}
            />
          ) : (
            <EmptyState
              title="No points activity yet"
              message="Your points earning and redemption history will appear here."
            />
          )}
        </div>
      </Container>

      <ActivityDetailModal
        activity={selectedActivity}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default PointsActivityPage;
